import { useMutation, useQuery } from "@tanstack/react-query";
import {
  getLatestRefundInfoByPaymentCodeKey,
  getRefundInfoKey,
  getRefundListKey,
  getServiceConfigurationKey,
  getUserConfigurationKey,
  getWalletBalanceKey,
} from "./key";
import api from "../repository";
import {
  CreateRefundRequest,
  CreateWithdrawRequest,
  InitializeUserRequest,
  WagePayerEnum,
} from "../repository/refund/v1/refund-v1-api";
import { CreateRefundMutation, GetRefundListRequest, createRechargeRequest } from "../domains/use-case.domain";

export function useGetUserConfiguration() {
  return useQuery({
    queryKey: getUserConfigurationKey(),
    queryFn: api.v1.getUserConfigurationWithCancellationTokenGet,
    staleTime: 1 * 60 * 1000, // Cache data for 1 minute
  });
}

export function useInitializeUser() {
  return useMutation({
    mutationFn: (data: InitializeUserRequest) => api.v1.initializeUserWithRequestModelWithCancellationTokenPost(data),
  });
}

export function useGetServiceConfiguration() {
  const serviceConfiguration = useQuery({
    queryKey: getServiceConfigurationKey(),
    queryFn: api.v1.getServiceConfigurationWithCancellationTokenGet,
    staleTime: 1 * 60 * 60 * 1000, // Cache data for 1 hour
  });

  return {
    isLoading: serviceConfiguration.isLoading,
    data: serviceConfiguration.data?.data,
  };
}

export function useCreateRefund() {
  const createRefund = useMutation({
    mutationFn: (data: CreateRefundRequest) => api.v1.createRefundWithRequestModelWithCancellationTokenPost(data),
  });
  // const createRefundWithPan = useMutation({
  //   mutationFn: (data: CreateRefundWithPanRequest) =>
  //     api.v1.createRefundWithPanWithRequestModelWithCancellationTokenPost(data),
  // });

  const mutateAsync = ({ amount, paymentCode, wagePayer = WagePayerEnum.Business }: CreateRefundMutation) =>
    createRefund.mutateAsync({ amount, paymentCode, wagePayer });
  // return createRefundWithPan.mutateAsync({
  //   amount,
  //   pan,
  //   paymentCode,
  //   wagePayer,
  // });
  // const isPending = createRefund.isPending || createRefundWithPan.isPending;

  return { mutateAsync, isPending: createRefund.isPending };
}

export function useGetRefundList(data: GetRefundListRequest) {
  const refundList = useQuery({
    queryKey: getRefundListKey(data),
    queryFn: () =>
      api.v1.getRefundListWithRequestModelWithCancellationTokenPost({
        pageSize: data.PageSize,
        pageNumber: data.PageNumber,
        paymentCodes: data.PaymentCodes,
        states: data.States,
        creationDateFrom: data.CreationDateFrom,
        creationDateTo: data.CreationDateTo,
        minAmount: data.MinAmount,
        maxAmount: data.MaxAmount,
        pan: data.Pan,
      }),
  });

  return {
    data: refundList.data?.data,
    isLoading: refundList.isLoading,
    error: refundList.error,
    refetch: refundList.refetch,
  };
}

export function useGetRefundInfoWithCode(code: string) {
  const refundInfo = useQuery({
    queryKey: getRefundInfoKey(code),
    queryFn: () => api.v1.getRefundInfoWithCodeWithCancellationTokenGet(code),
    staleTime: 15 * 60 * 1000, // Cache data for 15 minute
    enabled: !!code,
  });

  return {
    data: refundInfo.data?.data,
    isLoading: refundInfo.isLoading,
    error: refundInfo.error,
    refetch: refundInfo.refetch,
  };
}

export function useGetWalletBalance() {
  const walletBalance = useQuery({
    queryKey: getWalletBalanceKey(),
    queryFn: api.v1.getWalletBalanceWithCancellationTokenGet,
    staleTime: 15 * 60 * 1000, // Cache data for 15 minute
  });

  return {
    data: walletBalance.data?.data,
    isLoading: walletBalance.isLoading,
    refetch: walletBalance.refetch,
  };
}

export function useCreateRecharge() {
  return useMutation({
    mutationFn: (data: createRechargeRequest) =>
      api.v1.createRechargeOperationWithAmountWithCancellationTokenPost(data),
  });
}

export function useGetLatestRefundInfoByPaymentCode(code: string) {
  const refundInfo = useQuery({
    queryKey: getLatestRefundInfoByPaymentCodeKey(code),
    queryFn: () => api.v1.getLastetRefundInfoByPaymentCodeWithPaymentCodeWithCancellationTokenGet(code),
    staleTime: 0,
    enabled: !!code,
  });

  return {
    data: refundInfo.data?.data,
    isLoading: refundInfo.isLoading,
    error: refundInfo.error,
  };
}

export function useCreateWithdraw() {
  return useMutation({
    mutationFn: (data: CreateWithdrawRequest) => api.v1.createWithdrawWithRequestModelWithCancellationTokenPost(data),
  });
}

export function useConfirmWithdraw() {
  return useMutation({
    mutationFn: (code: string) => api.v1.confirmWithdrawWithCodeWithCancellationTokenPost(code),
  });
}

export function useCancelRefund() {
  const cancelRefund = useMutation({
    mutationFn: (code: string) => api.v1.cancelRefundWithCodeWithCancellationTokenPost(code),
  });

  return {
    mutateAsync: cancelRefund.mutateAsync,
    isPending: cancelRefund.isPending,
  };
}

export function useReinitializeRefund() {
  const reinitializeRefund = useMutation({
    mutationFn: (code: string) => api.v1.reinitializeRefundWithCodeWithCancellationTokenPost(code),
  });

  return {
    mutateAsync: reinitializeRefund.mutateAsync,
    isPending: reinitializeRefund.isPending,
  };
}
